import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const AUTH_API_BASE = `${API_ROOT}/api/auth`;
const ADVANCED_ROLE_ALLOWED_MODULES = [1, 3, 7, 13, 16, 14];

export default function SwitchRole() {
  const navigate = useNavigate();
  const storedUser = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const [mobile] = useState(storedUser?.mobile || "");
  const [moduleId, setModuleId] = useState("");
  const [modules, setModules] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const currentModuleId = parseInt(storedUser?.moduleId, 10);

  useEffect(() => {
    if (!ADVANCED_ROLE_ALLOWED_MODULES.includes(currentModuleId)) {
      navigate("/basic-role", { replace: true });
      return;
    }

    const fetchAdvancedModules = async () => {
      if (!mobile) {
        setError("Missing mobile in session. Please login again.");
        setLoading(false);
        return;
      }

      try {
        const res = await fetch(`${AUTH_API_BASE}/advanced-modules?login=${encodeURIComponent(mobile)}`);
        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.error || data.message || "Failed to fetch advanced roles");
        }

        setModules(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err.message || "Failed to fetch advanced roles");
      } finally {
        setLoading(false);
      }
    };

    fetchAdvancedModules();
  }, [mobile, currentModuleId, navigate]);

  const handleSwitch = async (e) => {
    e.preventDefault();
    setError("");

    if (!moduleId) {
      setError("Please select module.");
      return;
    }

    try {
      setSubmitting(true);
      const res = await fetch(`${AUTH_API_BASE}/switch-role`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          login: mobile,
          moduleId,
          currentModuleId: storedUser?.moduleId
        })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Failed to switch role");
      }

      localStorage.setItem("user", JSON.stringify(data));
      const resolvedModuleId = parseInt(data?.moduleId, 10);
      const target = resolvedModuleId === 1
        ? "/profile"
        : resolvedModuleId === 10
          ? "/guesthouse/pending-bookings"
          : resolvedModuleId === 26
            ? "/dr-colleges"
            : "/dashboard";
      navigate(target);
    } catch (err) {
      setError(err.message || "Failed to switch role");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container" style={{ maxWidth: "560px" }}>
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <h3 className="mb-0">Assigned Roles</h3>
        <Link to="/basic-role" className="btn btn-outline-secondary btn-sm">
          Back to Employee Role
        </Link>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <form onSubmit={handleSwitch}>
        <div className="mb-3">
          <label className="form-label">User Login (Mobile)</label>
          <input className="form-control" value={mobile} readOnly />
        </div>

        <div className="mb-3">
          <label className="form-label">Select Module</label>
          <select
            className="form-control"
            value={moduleId}
            onChange={(e) => setModuleId(e.target.value)}
            required
            disabled={loading || !modules.length}
          >
            <option value="">Select Module</option>
            {modules.map((m) => (
              <option key={`${m.ModuleID}-${m.UserID}`} value={m.ModuleID}>
                {m.Module}
              </option>
            ))}
          </select>
        </div>

        <button className="btn btn-primary" type="submit" disabled={loading || submitting || !modules.length}>
          {submitting ? "Switching..." : "Switch"}
        </button>
      </form>
    </div>
  );
}
